/**
 * ContractorRfqsBoard
 * -------------------
 * Table view of every maintenance RFQ this contractor has been invited to bid
 * on. Each row shows the hospital, scope summary, priority, and the
 * contractor's own quote status. Inline actions per row:
 *
 *   - Submit Quote   (no quote yet)
 *   - Edit Quote     (a draft quote exists and not awarded)
 *   - Decline        (any non-awarded RFQ)
 *   - Withdraw       (delete a submitted-but-not-awarded quote)
 *   - View PO email  (after award — opens mailto with awarded amount)
 *
 * All actions hit /api/contractor/rfqs/... which carries the contractor
 * identity off their portal JWT — no manual contractor-info entry.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Inbox,
  Search,
  Send,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  Loader2,
  Award,
  MapPin,
  Building2,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  new:                 { label: "Open",         cls: "bg-blue-50 text-blue-700 border-blue-200" },
  dispatching:         { label: "Open",         cls: "bg-blue-50 text-blue-700 border-blue-200" },
  sent_to_contractors: { label: "Awaiting bid", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  quotes_received:     { label: "Bids in",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  awarded:             { label: "Awarded",      cls: "bg-violet-50 text-violet-700 border-violet-200" },
  cancelled:           { label: "Cancelled",    cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const QUOTE_META = {
  new:           { label: "Submitted",    cls: "bg-emerald-50 text-emerald-700" },
  awaiting_info: { label: "Needs info",   cls: "bg-amber-50 text-amber-700" },
  awarded:       { label: "🏆 Awarded",   cls: "bg-violet-100 text-violet-700" },
  not_selected: { label: "Not selected", cls: "bg-slate-100 text-slate-600" },
  declined:      { label: "Declined",     cls: "bg-rose-50 text-rose-700" },
};

const PRIORITY_META = {
  emergency: "bg-red-100 text-red-700",
  urgent:    "bg-red-100 text-red-700",
  high:      "bg-orange-100 text-orange-700",
  normal:    "bg-blue-100 text-blue-700",
  low:       "bg-slate-100 text-slate-600",
};

function fmtAge(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ContractorRfqsBoard({ token, contractor }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | open | submitted | awarded | declined
  const [editing, setEditing] = useState(null); // rfq currently in the modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contractor/rfqs`, { headers });
      setRfqs(r.data.rfqs || []);
    } catch (_) {
      toast.error("Could not load your RFQs");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const base = { all: rfqs.length, open: 0, submitted: 0, awarded: 0, declined: 0 };
    rfqs.forEach((r) => {
      const qs = r.my_quote?.status;
      if (qs === "awarded") base.awarded += 1;
      else if (qs === "declined") base.declined += 1;
      else if (qs) base.submitted += 1;
      else base.open += 1;
    });
    return base;
  }, [rfqs]);

  const filtered = useMemo(() => {
    let arr = rfqs;
    if (filter !== "all") {
      arr = arr.filter((r) => {
        const qs = r.my_quote?.status;
        if (filter === "open") return !qs;
        if (filter === "submitted") return qs && !["awarded", "declined", "not_selected"].includes(qs);
        if (filter === "awarded") return qs === "awarded";
        if (filter === "declined") return qs === "declined";
        return true;
      });
    }
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) =>
        [r.id, r.hospital_name, r.description, r.facility_name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    return arr;
  }, [rfqs, filter, search]);

  const decline = async (rfq) => {
    if (!window.confirm(`Decline RFQ ${rfq.id}? Admin will see you've opted out.`)) return;
    try {
      await axios.post(`${API}/contractor/rfqs/${rfq.id}/decline`, {}, { headers });
      toast.success("Declined");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decline failed");
    }
  };

  const withdraw = async (rfq) => {
    if (!window.confirm(`Withdraw your $${(rfq.my_quote?.total_estimate || 0).toLocaleString()} bid on ${rfq.id}?`)) return;
    try {
      await axios.delete(`${API}/contractor/rfqs/${rfq.id}/quote`, { headers });
      toast.success("Quote withdrawn");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Withdraw failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="contractor-rfqs-board">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">My RFQs</h1>
        <p className="text-sm text-slate-500">
          Every maintenance request you've been invited to bid on. Submit, edit, or decline directly here — no need to dig through email.
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { key: "all",       label: "All",       icon: Inbox },
          { key: "open",      label: "Awaiting your bid", icon: Send },
          { key: "submitted", label: "Submitted", icon: CheckCircle2 },
          { key: "awarded",   label: "Awarded",   icon: Award },
          { key: "declined",  label: "Declined",  icon: X },
        ].map((k) => {
          const Icon = k.icon;
          const active = filter === k.key;
          return (
            <button
              key={k.key}
              onClick={() => setFilter(k.key)}
              className={`text-left rounded-xl border p-3 transition-all ${
                active
                  ? "border-emerald-400 ring-2 ring-emerald-100"
                  : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
              } bg-white`}
              data-testid={`rfq-kpi-${k.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-slate-600" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.label}</span>
              </div>
              <div className="text-2xl font-extrabold text-slate-900 leading-none mt-1">{stats[k.key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by hospital, ID, scope…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="rfq-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left">RFQ</th>
                <th className="px-4 py-2 text-left">Hospital · Site</th>
                <th className="px-4 py-2 text-left">Scope</th>
                <th className="px-4 py-2 text-left">Priority</th>
                <th className="px-4 py-2 text-left">My quote</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  No RFQs match. When admin sends you an RFQ, it'll appear here.
                </td></tr>
              )}
              {!loading && filtered.map((r) => {
                const qs = r.my_quote?.status;
                const meta = QUOTE_META[qs];
                const wo = STATUS_META[r.status] || STATUS_META.new;
                return (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`rfq-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-mono text-xs text-slate-700">{r.id}</p>
                      <p className="text-[10px] text-slate-400">{fmtAge(r.invited_at || r.created_at)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-900 flex items-center gap-1 max-w-[200px] truncate">
                        <Building2 className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        {r.hospital_name || "—"}
                      </p>
                      {r.facility_name && <p className="text-xs text-slate-500 truncate max-w-[200px]">{r.facility_name}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-700 truncate max-w-[260px]">
                        {r.items?.length > 0
                          ? r.items.map((i) => (i.request_type || "issue").replace(/_/g, " ")).join(" · ")
                          : (r.request_type || "—").replace(/_/g, " ")}
                      </p>
                      {r.location && (
                        <p className="text-xs text-slate-500 flex items-center gap-0.5 truncate max-w-[260px]">
                          <MapPin className="w-3 h-3" /> {r.location}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${PRIORITY_META[r.priority] || "bg-slate-100 text-slate-600"}`}>
                        {r.priority || "normal"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.my_quote ? (
                        <>
                          <p className="text-sm font-semibold text-slate-900">
                            ${Number(r.my_quote.total_estimate || 0).toLocaleString()}
                          </p>
                          {meta && (
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.cls}`}>
                              {meta.label}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No bid yet</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${wo.cls}`}>
                        {wo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {qs === "awarded" ? (
                          <span className="text-emerald-700 text-xs font-bold">🏆 Won</span>
                        ) : qs === "declined" || r.status === "cancelled" ? (
                          <span className="text-slate-400 text-xs italic">—</span>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              onClick={() => setEditing(r)}
                              className="bg-amber-600 hover:bg-amber-700 gap-1 h-7 px-2.5 text-xs"
                              data-testid={`rfq-quote-btn-${r.id}`}
                            >
                              {r.my_quote ? <><Pencil className="w-3 h-3" /> Edit</> : <><Send className="w-3 h-3" /> Submit</>}
                            </Button>
                            {r.my_quote && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => withdraw(r)}
                                className="gap-1 h-7 px-2.5 text-xs border-slate-300"
                                data-testid={`rfq-withdraw-btn-${r.id}`}
                              >
                                <Trash2 className="w-3 h-3" /> Withdraw
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => decline(r)}
                              className="gap-1 h-7 px-2.5 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                              data-testid={`rfq-decline-btn-${r.id}`}
                            >
                              <X className="w-3 h-3" /> Decline
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <QuoteSheet
          rfq={editing}
          contractor={contractor}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Quote submit / edit modal
// ============================================================================
function QuoteSheet({ rfq, contractor, token, onClose, onSaved }) {
  const existing = rfq.my_quote || {};
  const [labor, setLabor] = useState(existing.labor_total ?? "");
  const [parts, setParts] = useState(existing.parts_total ?? "");
  const [total, setTotal] = useState(existing.total_estimate ?? "");
  const [leadTime, setLeadTime] = useState(existing.lead_time ?? "");
  const [earliest, setEarliest] = useState(existing.earliest_start ?? "");
  const [notes, setNotes] = useState(existing.notes ?? "");
  const [saving, setSaving] = useState(false);

  const onMoney = (which, v) => {
    if (which === "labor") setLabor(v);
    else setParts(v);
    const l = which === "labor" ? Number(v || 0) : Number(labor || 0);
    const p = which === "parts" ? Number(v || 0) : Number(parts || 0);
    if (!Number.isNaN(l + p) && (l || p)) setTotal(String(l + p));
  };

  const save = async () => {
    if (!total && !labor && !parts) {
      toast.error("Provide a total or labor/parts breakdown");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/contractor/rfqs/${rfq.id}/quote`,
        {
          labor_total: labor ? Number(labor) : undefined,
          parts_total: parts ? Number(parts) : undefined,
          total_estimate: total ? Number(total) : undefined,
          lead_time: leadTime || undefined,
          earliest_start: earliest || undefined,
          notes: notes || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(existing.id ? "Quote updated" : "Quote submitted");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="rfq-quote-sheet">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-white">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Send className="w-4 h-4 text-amber-600" /> {existing.id ? "Edit" : "Submit"} Quote — {rfq.id}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Scope · Priority {rfq.priority || "normal"}
            </div>
            {(rfq.items?.length ? rfq.items : []).map((it, idx) => (
              <div key={idx} className="text-xs text-slate-700 py-1 border-t border-slate-100 first:border-t-0">
                <strong>{(it.request_type || "issue").replace(/_/g, " ")}</strong>
                {it.location_details && <span className="text-slate-500"> · {it.location_details}</span>}
                <p className="text-slate-600">{it.description}</p>
              </div>
            )) || <p className="text-sm text-slate-700">{rfq.description}</p>}
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Labor ($)</Label>
              <Input type="number" min="0" step="0.01" value={labor} onChange={(e) => onMoney("labor", e.target.value)} data-testid="rfq-quote-labor" />
            </div>
            <div>
              <Label className="text-xs">Parts ($)</Label>
              <Input type="number" min="0" step="0.01" value={parts} onChange={(e) => onMoney("parts", e.target.value)} data-testid="rfq-quote-parts" />
            </div>
            <div>
              <Label className="text-xs">Total ($) *</Label>
              <Input type="number" min="0" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} required data-testid="rfq-quote-total" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Lead time</Label>
              <Input value={leadTime} onChange={(e) => setLeadTime(e.target.value)} placeholder="3-5 business days" />
            </div>
            <div>
              <Label className="text-xs">Earliest start</Label>
              <Input type="date" value={earliest} onChange={(e) => setEarliest(e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Assumptions, exclusions, COI status…" />
          </div>
          <p className="text-[11px] text-slate-500">
            Submitting as <strong>{contractor?.company_name || contractor?.name}</strong> · {contractor?.email}
          </p>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="rfq-quote-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {existing.id ? "Update" : "Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
}
